import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Target, Plus, ChevronsUpDown, Check } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator } from '@/components/ui/command';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
interface IdealProfile {
  id: string;
  name: string;
  dominance_min: number;
  dominance_max: number;
  extraversion_min: number;
  extraversion_max: number;
  patience_min: number;
  patience_max: number;
  formality_min: number;
  formality_max: number;
  objectivity_min: number;
  objectivity_max: number;
  company_id?: string;
}
interface IdealProfileSelectorProps {
  selectedProfile: IdealProfile | null;
  onProfileChange: (profile: IdealProfile | null) => void;
  compact?: boolean;
}
const IdealProfileSelector: React.FC<IdealProfileSelectorProps> = ({
  selectedProfile,
  onProfileChange,
  compact = false
}) => {
  const [profiles, setProfiles] = useState<IdealProfile[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  useEffect(() => {
    fetchIdealProfiles();
  }, []);
  const fetchIdealProfiles = async () => {
    try {
      // Get current user's role and company info
      const {
        data: {
          user
        }
      } = await supabase.auth.getUser();
      if (!user) {
        setProfiles([]);
        setIsLoading(false);
        return;
      }

      // Check if user is super admin
      const {
        data: superAdminCheck
      } = await supabase.rpc('is_super_admin', {
        _user_id: user.id
      });
      let query = supabase.from('ideal_profiles').select('*').order('name');

      // If not super admin, filter by company_id from active association
      if (!superAdminCheck) {
        const {
          data: association
        } = await supabase
          .from('user_company_associations')
          .select('company_id')
          .eq('user_id', user.id)
          .eq('is_active', true)
          .maybeSingle();
        
        if (association?.company_id) {
          query = query.eq('company_id', association.company_id);
        } else {
          // If user has no active company, only show profiles with no company_id
          query = query.is('company_id', null);
        }
      }
      const {
        data,
        error
      } = await query;
      if (error) {
        console.error('Error fetching ideal profiles:', error);
        toast({
          title: "Error",
          description: "Failed to load ideal profiles.",
          variant: "destructive"
        });
        return;
      }
      setProfiles(data || []);
    } catch (error) {
      console.error('Unexpected error:', error);
      toast({
        title: "Error",
        description: "An unexpected error occurred while loading profiles.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };
  const handleProfileSelect = (value: string) => {
    if (value === 'none') {
      onProfileChange(null);
      setOpen(false);
    } else if (value === 'create-new') {
      navigate('/ideal/new');
      setOpen(false);
    } else {
      const profile = profiles.find(p => p.id === value);
      onProfileChange(profile || null);
      setOpen(false);
    }
  };
  if (compact) {
    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={isLoading}
            className="h-10 px-3 py-2 gap-2 rounded-lg border-border bg-background hover:bg-accent/5 transition-colors justify-start"
          >
            <Target className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            <span className="truncate flex-1 text-left">
              {isLoading ? "Loading..." : selectedProfile?.name || "Profile matching"}
            </span>
            <ChevronsUpDown className="h-4 w-4 opacity-50 flex-shrink-0" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[300px] p-0 bg-background border-border" align="start">
          <Command>
            <CommandInput placeholder="Search profiles..." />
            <CommandList>
              <CommandEmpty>No profiles found.</CommandEmpty>
              <CommandGroup heading="Profiles">
                <CommandItem value="none" onSelect={handleProfileSelect}>
                  <Check className={cn("mr-2 h-4 w-4", !selectedProfile ? "opacity-100" : "opacity-0")} />
                  Select Ideal Profile
                </CommandItem>
                {profiles.map(profile => (
                  <CommandItem key={profile.id} value={profile.id} onSelect={handleProfileSelect}>
                    <Check className={cn("mr-2 h-4 w-4", selectedProfile?.id === profile.id ? "opacity-100" : "opacity-0")} />
                    {profile.name}
                  </CommandItem>
                ))}
              </CommandGroup>
              <CommandSeparator />
              <CommandGroup>
                <CommandItem value="create-new" onSelect={handleProfileSelect} className="text-primary">
                  <Plus className="h-4 w-4 mr-2" />
                  Create New Profile
                </CommandItem>
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={isLoading}
          className="w-64 h-10 px-3 py-2 rounded-lg border-border bg-background hover:bg-accent/5 transition-colors justify-between"
        >
          <span className="truncate">
            {isLoading ? "Loading profiles..." : selectedProfile?.name || "Select ideal profile"}
          </span>
          <ChevronsUpDown className="h-4 w-4 ml-2 opacity-50 flex-shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-0 bg-background border-border" align="start">
        <Command>
          <CommandInput placeholder="Search profiles..." />
          <CommandList>
            <CommandEmpty>No profiles found.</CommandEmpty>
            <CommandGroup heading="Profiles">
              <CommandItem value="none" onSelect={handleProfileSelect}>
                <Check className={cn("mr-2 h-4 w-4", !selectedProfile ? "opacity-100" : "opacity-0")} />
                Select Ideal Profile
              </CommandItem>
              {profiles.map(profile => (
                <CommandItem key={profile.id} value={profile.id} onSelect={handleProfileSelect}>
                  <Check className={cn("mr-2 h-4 w-4", selectedProfile?.id === profile.id ? "opacity-100" : "opacity-0")} />
                  {profile.name}
                </CommandItem>
              ))}
            </CommandGroup>
            <CommandSeparator />
            <CommandGroup>
              <CommandItem value="create-new" onSelect={handleProfileSelect} className="text-primary">
                <Plus className="h-4 w-4 mr-2" />
                Create New Profile
              </CommandItem>
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
export default IdealProfileSelector;